import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";
import Portal from "./Portal";
import Text from "./Text";
import { twMerge } from "tailwind-merge";
import Button, { type ButtonProps } from "./Button";

export interface ModalProps {
    // Controls whether the modal is open
    open: boolean;

    // Called when a close is requested
    onClose: () => void;

    // Additional class names for the modal container
    className?: string;

    // Additional class names for adjusting the overlay
    overlayClassName?: string;

    // Whether to show the close button in the top-right corner
    showCloseButton?: boolean;

    // When true, removes overflow-y-auto so children (e.g. dropdowns) can visually extend outside the modal bounds
    allowOverflow?: boolean;

    // Title, displayed at the top
    title?: React.ReactNode;

    // Description, displayed below the title
    description?: React.ReactNode;

    // Additional class names for the title
    titleClassName?: string;

    // Content
    children?: React.ReactNode;

    // Additional class names for the children container
    childrenClassName?: string;

    // Primary action button
    primaryButton?: ButtonProps;

    // Secondary action button
    secondaryButton?: ButtonProps;
}

const Modal: React.FC<ModalProps> = ({
    open,
    onClose,
    title,
    description,
    children,
    primaryButton,
    secondaryButton,
    titleClassName = "",
    className = "",
    childrenClassName = "",
    overlayClassName = "",
    showCloseButton = true,
    allowOverflow = false,
}) => {
    // Ref for the modal element
    const modalRef = useRef<HTMLDivElement>(null);

    // Close on escape key
    useEffect(() => {
        // Do nothing if modal is not open
        if (!open) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (open) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [open]);

    // Focus trap
    useEffect(() => {
        // Do nothing if modal is not open or modalRef is not set
        if (!open || !modalRef.current) {
            return;
        }

        // Save previously focused element to restore focus on close
        // Useful for accessibility in cases where modal is opened from a button or link
        const previouslyFocused = document.activeElement as HTMLElement;
        modalRef.current.focus();

        // Restore focus to previously focused element on close
        return () => {
            if (previouslyFocused) {
                previouslyFocused.focus();
            }
        };
    }, [open]);

    // If not open, render nothing
    if (!open) {
        return null;
    }

    return (
        <Portal>
            {/* Overlay */}
            <div
                className={twMerge("fixed inset-0 z-30 flex items-center justify-center bg-[#EDEDED]/70", overlayClassName)}
                aria-modal="true"
                role="dialog"
                aria-label={typeof title === "string" ? title : undefined}
                onMouseDown={e => {
                    // Close on mousedown instead of click so the overlay is still mounted
                    // when the click completes — prevents the click from passing through
                    // to elements behind the modal (e.g. RFQ list items triggering navigation)
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                {/* Modal */}
                <div
                    ref={modalRef}
                    tabIndex={-1}
                    className={twMerge(
                        `relative mx-4 flex max-h-[98dvh] w-[98vw] flex-col rounded-2xl border border-stone-200 bg-white p-5 shadow focus:outline-none sm:mx-0 sm:w-full sm:max-w-md md:h-fit md:max-w-lg lg:max-w-xl ${allowOverflow ? "overflow-visible" : "overflow-y-auto"}`,
                        className,
                    )}
                    onClick={e => e.stopPropagation()}
                >
                    {/* Close Button */}
                    {showCloseButton && (
                        <button onClick={onClose} className="absolute top-5 right-5 z-10 focus:outline-none" aria-label="Close modal">
                            <X className="h-4 w-4 stroke-stone-400" />
                        </button>
                    )}

                    {/* Title and Content */}
                    {title && (
                        <Text variant="body700" className={twMerge(description ? "mb-1" : "mb-3", titleClassName)}>
                            {title}
                        </Text>
                    )}
                    {description && (
                        <Text variant="body400" className="mt-1 mb-3 text-stone-600">
                            {description}
                        </Text>
                    )}
                    {children && <div className={twMerge((primaryButton || secondaryButton) && "mb-3", childrenClassName)}>{children}</div>}
                    {(primaryButton || secondaryButton) && (
                        <div className="mt-3 flex gap-3">
                            {primaryButton && <Button color="bronze" {...primaryButton} />}
                            {secondaryButton && <Button color="white" {...secondaryButton} />}
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    );
};

export default Modal;
